import detect from 'detect.js'

import { Cookies } from 'services/cookies'

/**
 * Builds analytics properties as specified in GRO-518
 * Only includes properties explicitly mentioned in the ticket
 *
 * @param {Object} options - Options for building properties
 * @param {Array<string>} options.entities - Array of entity names to include (e.g., ['event_type', 'global', 'org', 'user'])
 * @param {Object} options.eventType - Event type object from the booking app (required for 'event_type' entity)
 * @param {Object} options.event - Event object (required for 'event' entity)
 * @param {Object} options.invitee - Invitee object (required for 'invitee' entity)
 * @param {Object} options.currentUser - Current user object (optional, for 'user' entity)
 * @param {Object} options.profile - Profile object (optional, for owner user in 'user' entity)
 * @param {Object} options.additionalProperties - Additional properties to merge in
 * @returns {Object} Analytics properties object
 */
export const buildAnalyticsProperties = ({
  entities = [],
  eventType,
  event,
  invitee,
  currentUser,
  profile,
  additionalProperties = {}
}) => {
  const cookies = new Cookies()
  const calAnonymousId = cookies.getCookie('cal_anonymous_id') || null
  const ua = detect.parse(navigator.userAgent)

  // Determine device type - default to desktop since most desktop browsers
  // don't set ua.device.type (it's undefined), only mobile/tablet do
  let userDeviceType = 'desktop'
  if (ua.device.type === 'mobile') {
    userDeviceType = 'phone'
  } else if (ua.device.type === 'tablet') {
    userDeviceType = 'tablet'
  }

  const result = {}

  // Event Type properties (as specified in GRO-518)
  // owner_type, event_type_host_membership, managed_et_relationship, event_type_kind,
  // event_type_uuid, event_type_type, event_type_invitee_membership, event_type_host_assignment, event_type_id
  if (entities.includes('event_type') || entities.includes('event_types')) {
    if (eventType) {
      result.owner_type = eventType.owner_type?.toLowerCase() || null
      result.event_type_id = eventType.id || null
      result.event_type_uuid = eventType.uuid || null
      result.event_type_kind = eventType.kind || null
      result.event_type_type = eventType.type || null

      // event_type_host_membership
      if (eventType.pooling_type === 'multi_pool' || eventType.pooling_type === 'collective') {
        result.event_type_host_membership = 'collective'
      } else {
        result.event_type_host_membership = 'solo'
      }

      // event_type_invitee_membership
      result.event_type_invitee_membership = eventType.kind === 'group' ? 'group' : 'solo'

      // event_type_host_assignment
      if (eventType.pooling_type === 'multi_pool' || eventType.pooling_type === 'round_robin') {
        result.event_type_host_assignment = 'round_robin'
      } else {
        result.event_type_host_assignment = 'fixed'
      }

      // managed_et_relationship
      if (eventType.type === 'ManagedEventType') {
        result.managed_et_relationship = 'parent'
      } else if (eventType.parent_id) {
        result.managed_et_relationship = 'child'
      } else {
        result.managed_et_relationship = 'none'
      }
    } else {
      // Include null values for all event_type properties if entity is required but eventType is missing
      result.owner_type = null
      result.event_type_id = null
      result.event_type_uuid = null
      result.event_type_kind = null
      result.event_type_type = null
      result.event_type_host_membership = null
      result.event_type_invitee_membership = null
      result.event_type_host_assignment = null
      result.managed_et_relationship = null
    }
  }

  // Event properties (as specified in GRO-518 - not explicitly listed, but needed for Invitee Canceled)
  // Only include if events entity is required
  if (entities.includes('event') || entities.includes('events')) {
    // Note: event_id and event_uuid are not in the GRO-518 list, but may be needed for Invitee Canceled
    // Leaving empty for now - will be added when implementing Invitee Canceled if needed
  }

  // Invitee properties (as specified in GRO-518 - not explicitly listed, but needed for Invitee Canceled)
  // Only include if invitees entity is required
  if (entities.includes('invitee') || entities.includes('invitees')) {
    // Note: invitee_id and invitee_uuid are not in the GRO-518 list, but may be needed for Invitee Canceled
    // Leaving empty for now - will be added when implementing Invitee Canceled if needed
  }

  // Organization properties (as specified in GRO-518)
  // stage, tier, organization_total_seats
  // Note: organization_id is provided by global entity, not org entity
  if (entities.includes('org') || entities.includes('organization')) {
    const organization = eventType?.organization
    if (organization) {
      result.stage = organization.stage || null
      result.tier = organization.tier || null
      // organization_total_seats - not available in booking app blueprint, will be null
      result.organization_total_seats = null
    } else {
      result.stage = null
      result.tier = null
      result.organization_total_seats = null
    }
  }

  // User properties (as specified in GRO-518)
  // cal_anonymous_id, user_role, days_since_signup
  // Note: user_id and user_uuid are provided by global entity, not user entity
  if (entities.includes('user')) {
    result.cal_anonymous_id = calAnonymousId
    // user_role - not available in booking app user model, will be null
    result.user_role = null
    // days_since_signup - not available without created_at, will be null
    result.days_since_signup = null
  }

  // Global properties (as specified in GRO-518)
  // platform_type, user_device_type, destination, location, platform_details, timestamp
  // Note: destination and location are event-specific and should be in additionalProperties
  // Also includes organization_id and user_id/user_uuid (listed under Organization/User in ticket but provided by global entity)
  if (entities.includes('global')) {
    const user = profile || currentUser
    // Use undefined instead of null to match original behavior where missing values were undefined
    // This ensures Segment omits the property rather than sending null, matching historical behavior
    result.user_id = user?.id
    result.user_uuid = user?.uuid
    result.organization_id = eventType?.organization?.id
    result.platform_type = 'web_app'
    result.platform_details = 'web_app'
    result.user_device_type = userDeviceType
    result.timestamp = new Date().toISOString()
  }

  return {
    ...result,
    ...additionalProperties
  }
}
